"use client";

import {
  motion,
  useAnimation,
  useMotionValue,
  useTransform,
} from "framer-motion";
import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useId,
  useCallback,
} from "react";
import clsx from "clsx";

type Side = "left" | "right" | "none";

/** ---------- Global bus: only-one-open logic (HMR-safe) ---------- */
type BusEvent =
  | { type: "open"; id: string; group: string }
  | { type: "close"; id: string; group: string };

declare global {
  interface Window {
    __SWIPE_BUS__?: {
      subscribe: (cb: (e: BusEvent) => void) => () => void;
      publish: (e: BusEvent) => void;
    };
  }
}

const makeBus = () => {
  const listeners = new Set<(e: BusEvent) => void>();
  return {
    subscribe(cb: (e: BusEvent) => void) {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    publish(e: BusEvent) {
      listeners.forEach((cb) => cb(e));
    },
  };
};

const BUS =
  (typeof window !== "undefined" && (window.__SWIPE_BUS__ ||= makeBus())) ||
  makeBus();

/** ---------------------------------------------------------------- */

export function SwipeableItem({
  children,
  leftActions,
  rightActions,
  onOpenSide,
  onDismiss,
  actionWidth = "auto",
  dismissThreshold = 0.55,
  flickVel = 800,
  className,
  disabled = false,
  dismissable = false,
  group = "default",
}: {
  children: React.ReactNode;
  leftActions?: React.ReactNode;
  rightActions?: React.ReactNode;
  onOpenSide?: (side: Side) => void;
  onDismiss?: () => void;
  actionWidth?: number | "auto";
  dismissThreshold?: number;
  flickVel?: number;
  /** @deprecated Action rails now use physical left/right placement in every direction. */
  rtl?: boolean;
  className?: string;
  disabled?: boolean;
  /** If false, the item always snaps back to closed on drag end. */
  dismissable?: boolean;
  group?: string;
}) {
  const id = useId();
  const controls = useAnimation();
  const x = useMotionValue(0);
  const suppressClickRef = useRef(false);
  const suppressTimerRef = useRef<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);

  const [openSide, setOpenSide] = useState<Side>("none");
  const isOpen = openSide !== "none";

  const [leftW, setLeftW] = useState(0);
  const [rightW, setRightW] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (el) el.style.touchAction = "pan-y";
  }, []);

  useEffect(() => {
    return () => {
      if (suppressTimerRef.current) {
        window.clearTimeout(suppressTimerRef.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (actionWidth !== "auto") return;

    const update = () => {
      if (leftRef.current)
        setLeftW(leftRef.current.getBoundingClientRect().width);
      if (rightRef.current)
        setRightW(rightRef.current.getBoundingClientRect().width);
    };

    update();

    const ro = new ResizeObserver(update);
    if (leftRef.current) ro.observe(leftRef.current);
    if (rightRef.current) ro.observe(rightRef.current);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [actionWidth]);

  const fallback = typeof actionWidth === "number" ? actionWidth : 96;
  const leftTarget = actionWidth === "auto" ? leftW || fallback : actionWidth;
  const rightTarget = actionWidth === "auto" ? rightW || fallback : actionWidth;

  const MIN_SCALE = 0.7; // how small it gets at the "dip"
  const DIP_POINT = 0.2; // fraction of drag where min scale happens (0–1)

  /**
   * Turns [0..1] progress into a V-shaped scale:
   *  t = 0   → 1
   *  t = d   → MIN_SCALE
   *  t = 1   → 1
   */
  function vShapeScale(t: number) {
    if (t <= 0) return 1;
    if (t >= 1) return 1;

    if (t <= DIP_POINT) {
      const localT = t / DIP_POINT; // 0 → 1
      return 1 - (1 - MIN_SCALE) * localT; // 1 → MIN_SCALE
    } else {
      const localT = (t - DIP_POINT) / (1 - DIP_POINT); // 0 → 1
      return MIN_SCALE + (1 - MIN_SCALE) * localT; // MIN_SCALE → 1
    }
  }

  const leftScale = useTransform(x, (v) => {
    const normalized = v; // >0 when dragging toward the physical left action rail
    const max = Number(leftTarget) || 1;
    if (max <= 0) return 1;

    const clamped = Math.min(Math.max(normalized, 0), max);
    const t = clamped / max; // 0 → 1
    return vShapeScale(t);
  });

  const rightScale = useTransform(x, (v) => {
    const normalized = -v; // >0 when dragging toward the physical right action rail
    const max = Number(rightTarget) || 1;
    if (max <= 0) return 1;

    const clamped = Math.min(Math.max(normalized, 0), max);
    const t = clamped / max; // 0 → 1
    return vShapeScale(t);
  });

  const isInsideAllowed = useCallback((node: EventTarget | null) => {
    if (!(node instanceof Node)) return false;
    const roots = [leftRef.current, rightRef.current].filter(
      (n): n is HTMLDivElement => !!n,
    );
    return roots.some((root) => root.contains(node));
  }, []);

  const snapTo = useCallback(
    async (targetX: number) =>
      controls.start({
        x: targetX,
        transition: { type: "spring", stiffness: 500, damping: 40 },
      }),
    [controls],
  );

  const close = useCallback(
    async (silent?: boolean) => {
      setOpenSide("none");
      onOpenSide?.("none");
      if (!silent) BUS.publish({ type: "close", id, group });
      await snapTo(0);
    },
    [group, id, onOpenSide, snapTo],
  );

  const openLeft = useCallback(async () => {
    BUS.publish({ type: "open", id, group });
    setOpenSide("left");
    onOpenSide?.("left");
    await snapTo(Number(leftTarget));
  }, [group, id, leftTarget, onOpenSide, snapTo]);

  const openRight = useCallback(async () => {
    BUS.publish({ type: "open", id, group });
    setOpenSide("right");
    onOpenSide?.("right");
    await snapTo(-Number(rightTarget));
  }, [group, id, rightTarget, onOpenSide, snapTo]);

  useEffect(() => {
    if (disabled) close(true);
  }, [disabled, close]);

  // Freeze page & ESC while open
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    (document.activeElement as HTMLElement | null)?.blur?.();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
    };
    document.addEventListener("keydown", onKey, { capture: true });
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey, { capture: true } as any);
    };
  }, [close, isInsideAllowed, isOpen]);

  // Only-one-open per group
  useEffect(() => {
    return BUS.subscribe((evt) => {
      if (evt.group !== group) return;
      if (evt.type === "open" && evt.id !== id) {
        if (isOpen) close(true);
      }
    });
  }, [group, id, isOpen, close]);

  // --- Strict click-outside guard (hardened) ---
  useEffect(() => {
    if (!isOpen) return;

    // One guard function we reuse
    const guard = (e: Event) => {
      // allow interactions inside action areas ONLY
      if (isInsideAllowed(e.target)) return;

      // nuke the event so nothing else handles it
      e.preventDefault();
      e.stopPropagation();
      // hard stop for React synthetic / other native listeners
      if (typeof (e as any).stopImmediatePropagation === "function") {
        (e as any).stopImmediatePropagation();
      }
      // older code sometimes checks this flag
      (e as any).cancelBubble = true;

      close();
    };

    // Cover all the phases/targets that might see the event first
    const targets: EventTarget[] = [
      window,
      document,
      document.documentElement,
      document.body,
    ].filter(Boolean) as EventTarget[];

    // Events to block. We include both pointer and mouse + click trio.
    const events: Array<keyof DocumentEventMap> = [
      "pointerdown",
      "pointerup",
      "click",
      "mousedown",
      "mouseup",
      "touchstart",
      "contextmenu",
      "wheel",
      "focusin",
    ];

    // Attach on CAPTURE first (runs before React)
    targets.forEach((t) => {
      events.forEach((type) =>
        t.addEventListener(type, guard, { capture: true, passive: false }),
      );
    });

    // Also attach a bubble-phase guard as a belt-and-suspenders
    targets.forEach((t) => {
      events.forEach((type) =>
        t.addEventListener(type, guard as EventListener, {
          capture: false,
          passive: false,
        }),
      );
    });

    return () => {
      targets.forEach((t) => {
        events.forEach((type) => {
          t.removeEventListener(
            type,
            guard as EventListener,
            { capture: true } as any,
          );
          t.removeEventListener(
            type,
            guard as EventListener,
            { capture: false } as any,
          );
        });
      });
    };
  }, [close, isInsideAllowed, isOpen]);

  const handleDragEnd = async (
    _: any,
    info: { offset: { x: number }; velocity: { x: number } },
  ) => {
    if (disabled) return close();

    const cardWidth = containerRef.current?.offsetWidth ?? 1;
    const { x: offsetX } = info.offset;
    const velX = info.velocity.x;

    const normalizedX = offsetX;
    const normalizedVel = velX;
    const dist = Math.abs(normalizedX);

    // Any real drag should suppress click on release
    suppressClickRef.current = true;
    if (suppressTimerRef.current) {
      window.clearTimeout(suppressTimerRef.current);
    }
    suppressTimerRef.current = window.setTimeout(() => {
      suppressClickRef.current = false;
      suppressTimerRef.current = null;
    }, 160);

    // 1) Flick: open left/right if fast enough
    if (Math.abs(velX) > flickVel) {
      if (normalizedVel > 0) {
        if (leftActions) return openLeft();
      } else {
        if (rightActions) return openRight();
      }
    }

    // 2) Only dismissal is gated by `dismissable`
    const shouldDismiss = dismissable && dist > cardWidth * dismissThreshold;

    if (shouldDismiss) {
      const target = normalizedX > 0 ? cardWidth : -cardWidth;
      await controls.start({
        x: target,
        transition: { type: "tween", duration: 0.18 },
      });
      onDismiss?.();
      return;
    }

    // 3) Otherwise, decide whether to open left/right or close
    if (normalizedX > (Number(leftTarget) || 0) / 2 && leftActions) {
      return openLeft();
    }

    if (normalizedX < -(Number(rightTarget) || 0) / 2 && rightActions) {
      return openRight();
    }

    return close();
  };

  return (
    <div
      ref={containerRef}
      className={clsx(
        "relative select-none",
        isOpen ? "z-10" : "z-auto",
        className,
      )}
      role="group"
      aria-roledescription="swipeable row"
    >
      {/* Actions layer */}
      <div className="absolute inset-0 z-10 overflow-hidden">
        {leftActions ? (
          <motion.div
            className="absolute inset-y-0 left-0 flex items-center pl-3"
            style={{
              pointerEvents: disabled ? "none" : "auto",
              scale: leftScale,
              transformOrigin: "left center",
            }}
          >
            {/* INNER: unscaled, used for measurement */}
            <div
              ref={leftRef}
              className="flex items-center gap-2 px-3"
              style={{
                width:
                  actionWidth === "auto" ? undefined : (actionWidth as number),
              }}
            >
              {leftActions}
            </div>
          </motion.div>
        ) : null}

        {rightActions ? (
          <motion.div
            className="absolute inset-y-0 right-0 flex items-center justify-end"
            style={{
              pointerEvents: disabled ? "none" : "auto",
              scale: rightScale,
              transformOrigin: "right center",
            }}
          >
            {/* INNER: unscaled, used for measurement */}
            <div
              ref={rightRef}
              className="flex items-center justify-end gap-2 py-[1px]"
              style={{
                width:
                  actionWidth === "auto" ? undefined : (actionWidth as number),
              }}
            >
              {rightActions}
            </div>
          </motion.div>
        ) : null}
      </div>

      {/* Foreground card */}
      <motion.div
        drag={disabled ? false : "x"}
        dragElastic={0.12}
        dragMomentum
        onDragStart={() => {
          suppressClickRef.current = true;
          if (suppressTimerRef.current) {
            window.clearTimeout(suppressTimerRef.current);
          }
        }}
        onDragEnd={handleDragEnd}
        animate={controls}
        dragConstraints={{ left: -240, right: 240 }}
        className="relative z-20 will-change-transform"
        style={{ touchAction: "pan-y", x }}
        onClickCapture={(e) => {
          if (
            suppressClickRef.current ||
            (isOpen && !isInsideAllowed(e.target as EventTarget))
          ) {
            e.preventDefault();
            e.stopPropagation();
            (e as any).stopImmediatePropagation?.();
          }
        }}
      >
        {/* Block inner interactions while open */}
        <div className={isOpen ? "pointer-events-none select-none" : ""}>
          {children}
        </div>
      </motion.div>
    </div>
  );
}
